import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(() => {
        try { return JSON.parse(localStorage.getItem("icfw_user") || "null"); } catch { return null; }
    });
    const [loading, setLoading] = useState(false);

    const _finalizeLogin = useCallback((data) => {
        localStorage.setItem("icfw_token", data.token);
        localStorage.setItem("icfw_user", JSON.stringify(data.user));
        setUser(data.user);
        return data.user;
    }, []);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        if (data.mfa_required) return { mfa_required: true, challenge: data.mfa_challenge_token };
        return _finalizeLogin(data);
    }, [_finalizeLogin]);

    const verifyMfa = useCallback(async (challenge, code) => {
        const { data } = await api.post("/auth/login/verify-totp", {
            mfa_challenge_token: challenge, code,
        });
        return _finalizeLogin(data);
    }, [_finalizeLogin]);

    const signup = useCallback(async (payload) => {
        await api.post("/auth/register", payload);
        return login(payload.email, payload.password);
    }, [login]);

    const logout = useCallback(() => {
        localStorage.removeItem("icfw_token");
        localStorage.removeItem("icfw_user");
        setUser(null);
    }, []);

    const refreshMe = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            localStorage.setItem("icfw_user", JSON.stringify(data));
            setUser(data);
        } catch { logout(); }
    }, [logout]);

    const updateUser = useCallback((patch) => {
        setUser(prev => {
            const next = { ...(prev || {}), ...(typeof patch === "function" ? patch(prev) : patch) };
            localStorage.setItem("icfw_user", JSON.stringify(next));
            return next;
        });
    }, []);

    useEffect(() => {
        const t = localStorage.getItem("icfw_token");
        if (t && !user) refreshMe();
    }, [user, refreshMe]);

    return (
        <AuthContext.Provider value={{ user, login, verifyMfa, signup, logout, refreshMe, updateUser, setUser: updateUser, loading, setLoading }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
